import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider, App as AntdApp } from 'antd';
import viVN from 'antd/locale/vi_VN';
import dayjs from 'dayjs';
import { SystemSettingsProvider } from '@/contexts/SystemSettingsContext';
import 'dayjs/locale/vi';

import App from '@/app/App';
import { antdTheme } from '@/app/theme';
import './styles/index.css';

dayjs.locale('vi');

// ============================================================
// V1.1.0 (PWA) — iOS standalone: gắn class lên <html> để CSS chừa
// safe-area phía trên. Cần vì một số bản iOS không khớp media query
// `display-mode: standalone`, chỉ đặt navigator.standalone.
// Không có nó thì status bar (đồng hồ/pin) đè lên header và nút menu
// nhìn thấy nhưng bấm không được.
// ============================================================
{
  const nav = window.navigator as unknown as { standalone?: boolean };
  const isIosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const standalone =
    nav.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  if (isIosDevice && standalone) {
    document.documentElement.classList.add('hv-ios-standalone');
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

async function prepare() {
  if (import.meta.env.VITE_USE_MSW === 'true') {
    const { startMockWorker } = await import('@/mocks/browser');
    await startMockWorker();
  }
}

prepare().then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider locale={viVN} theme={antdTheme}>
          <AntdApp>
            <SystemSettingsProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </SystemSettingsProvider>
          </AntdApp>
        </ConfigProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </React.StrictMode>,
  );
});
